<template>
    <div class="main-card">
        <div class="row page-titles">
            <div class="col-md-5 align-self-center">
                <h4 class="text-themecolor">
                    <i class="fas fa-chart-line"></i>
                    {{ $t('pages.Overview') }}
                </h4>
            </div>
            <div class="col-md-7 align-self-center text-right">
                <div class="d-flex justify-content-end align-items-center">
                    <breadcrumb :page="currentPage" />
                </div>
            </div>
        </div>
        <div class="card" v-if="defaultCode">
            <loading-container :loading="form.overviewLoading">
                <div class="card-header header-size">
                    <div class="header-row">
                        <span>
                            <b :class="{ 'trans-negative': summary.warning === true }">{{ summary.code }}</b>
                            <span v-if="summary.warning === true">
                                <IconTooltip iconClass="text-danger fas fa-exclamation-triangle m-l-5"
                                    :text="summary.warningMessage" />
                            </span>
                        </span>
                        <AddWatchList />
                        <span v-if="summary.hasData === false" @click="() => submitRequest(summary.code)">
                            <IconTooltip iconClass="fas fa-file-medical text-blue cursor-pointer"
                            :text="$t('messages.request_analysis')" />
                        </span>
                        <span>
                            <IconTooltip :iconClass="getTypeIcons(summary.type)"
                            :text="getBusinessTypeText(summary.type)" />
                        </span>
                        <span class="same-row">
                            <IconTooltip :iconClass="getSectorIcons(summary.sector)"
                                :text="getSectorText(summary.sector)" />
                            <a class="truncate-text-20 text-blue cursor-pointer" @click="showCompetitors = true"> {{
                                summary.industry }}</a>
                        </span>
                        <span v-if="summary.marketPrice">
                            <span class="price-change box-120" :class="{
                                'positive': summary.marketPrice.perChange > 0,
                                'negative': summary.marketPrice.perChange < 0 && summary.marketPrice.value > summary.marketPrice.floor,
                                'neutral': summary.marketPrice.perChange === 0,
                                'ceil': summary.marketPrice.value >= summary.marketPrice.ceil,
                                'floor': summary.marketPrice.value <= summary.marketPrice.floor
                            }">
                                <span>
                                    <i
                                        :class="{ 'fas fa-arrow-up': summary.marketPrice.perChange > 0, 'fas fa-arrow-down': summary.marketPrice.perChange < 0, 'fas fa-minus': summary.marketPrice.perChange === 0 }"></i>
                                    {{ summary.marketPrice.value | numberDisplay }}
                                </span>
                                <span>{{ summary.marketPrice.perChange / 100 | percentDisplay }} </span>
                            </span>
                        </span>
                    </div>

                </div>
                <div class="card-body">
                    <div class="row-align">
                        <span>
                            <span v-if="summary.latestReport">
                                {{ $t('common.YTD') }}: {{ summary.latestReport }}
                            </span>
                            <span @click="() => exportToFile(summary.code)">
                                <IconTooltip iconClass="text-blue cursor-pointer fas fa-download"
                                    text="Export financial statements" />
                            </span>
                        </span>
                        <span>
                            {{ $t('updated') }}: {{ summary.lastSync | datetimeDisplay }}
                        </span>
                    </div>
                    <div class="cards-container">
                        <span v-if="summary.marketPrice">
                            <CompactCard type="trading_view" @view-details="handleViewDetails" />
                        </span>
                        <span v-if="summary.hasData">
                            <CompactCard type="dividend" :dividend="summary.dividend" @view-details="handleViewDetails" />
                        </span>
                        <span v-if="summary.hasData">
                            <CompactCard type="dca" :dca="summary.dca" @view-details="handleViewDetails" />
                        </span>
                        <span v-if="summary.hasData">
                            <CompactCard type="mos" :mos="summary.mos" @view-details="handleViewDetails" />
                        </span>
                        <span v-if="summary.hasData">
                            <CompactCard type="competitors" :competitors="summary.competitors" @view-details="handleViewDetails" />
                        </span>
                        <span v-if="summary.hasData">
                            <CompactCard type="management" @view-details="handleViewDetails" />
                        </span>
                    </div>
                </div>
                <div v-if="showCandleStickChart === true">
                    <CandleStick :symbol="this.defaultCode" @close="showCandleStickChart = false" />
                </div>
                <div v-if="showFinancialRatios === true">
                    <FinancialRatio :symbol="this.defaultCode" @close="showFinancialRatios = false" />
                </div>
                <div v-if="showDCA === true">
                    <DCA :symbol="this.defaultCode" @close="showDCA = false" />
                </div>
                <div v-if="showMOS === true">
                    <MOS :symbol="this.defaultCode" @close="showMOS = false" />
                </div>
                <div v-if="showCompetitors === true">
                    <Competitor :symbol="this.defaultCode" @close="showCompetitors = false" />
                </div>
                <div v-if="showManagement === true">
                    <Management :symbol="this.defaultCode" @close="showManagement = false" />
                </div>
            </loading-container>
        </div>
        <div class="card card-body" v-else>
            No stock selected! Search a stock to get the analysis
        </div>
    </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import LoadingContainer from "@/components/Misc/LoadingContainer";
import CompactCard from '../../components/Common/CompactCard.vue';
import Breadcrumb from "@/components/Common/Breadcrumb";
import IconTooltip from '../../components/Common/IconTooltip.vue';
import CandleStick from "../../components/Chart/CandleStick.vue";
import FinancialRatio from "./components/FinancialRatio.vue";
import AddWatchList from "@/components/Common/AddWatchList";
import DCA from "./components/DCA.vue";
import MOS from "./components/MOS.vue";
import Competitor from "./components/Competitor.vue";
import common from "@/libs/common";
import i18n from '../../i18n';
import Management from "./components/Management.vue";

export default {
    components: {
        LoadingContainer,
        AddWatchList,
        CompactCard,
        Breadcrumb,
        IconTooltip,
        CandleStick,
        FinancialRatio,
        DCA,
        MOS,
        Competitor,
        Management
    },
    beforeMount() {
        if (this.$route.query.code) {
            this.setDefaultCode(this.$route.query.code);    
        }
        if (!this.symbol || this.symbol != this.defaultCode) {
            this.getOverview(this.defaultCode);
            this.symbol = this.defaultCode;
        }
    },
    data() {
        return {
            symbol: '',
            showCandleStickChart : false,
            showFinancialRatios : false,
            showDCA : false,
            showMOS : false,
            showCompetitors : false,
            showManagement : false
        };
    },
    computed: {
        ...mapGetters({
            form: "stock/form",
            summary: "stock/summary",
            defaultCode: "configuration/defaultCode",
        }),
        currentPage() {
            return i18n.t(`pages.${this.$store.state.route.meta.title}`);
        },
    },
    methods: {
        ...mapActions({
            getOverview: "stock/getOverview",
            setDefaultCode: "configuration/setDefaultCode",
            getCompetitors: "stock/getStockSameIndustryV2",
            requestNewStock: "stock/requestNewStock",
            exportToFile: "stock/exportToFile"
        }),
        submitRequest(code) {
            if (code) {
                this.requestNewStock(code);
            }
        },
        actionGetSameIndustry() {
            this.getCompetitors(this.symbol);
        },
        handleViewDetails(type) {
            switch (type) {
                case 'trading_view':
                    this.showCandleStickChart = true;
                    break;
                case 'dividend':
                    this.showFinancialRatios = true;
                    break;
                case 'dca':
                    this.showDCA = true;
                    break;
                case 'mos':
                    this.showMOS = true;
                    break;
                case 'competitors':
                    this.showCompetitors = true;
                    break;
                case 'management':
                    this.showManagement = true;
                    break;
            }
        },
        getFeatureNames(code) {
            common.getFeatureNames(code);
        },
        getTypeIcons(code) {
            return common.getTypeIcons(code);
        },
        getSectorIcons(code) {
            return common.getSectorIcons(code);
        },
        getSectorText(code) {
            return common.getSectorText(code);
        },
        getBusinessTypeText(code) {
            return common.getBusinessTypeText(code);
        },
        display20(text) {
            if (text.length > 20) {
                return text.substring(0, 20) + '...';
            }
        }
    }
};
</script>

<style scoped>
.main-card {
    padding: 0px 0px 0px 0px !important;
}

.header-size {
    font-size: 16px;
}

.header-row {
    display: flex;
    gap: 10px;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.group-element {
    margin: 0px 0px 30px 0px;
    gap: 10px;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.cards-container {
    margin-top: 20px;
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    grid-gap: 10px;
    /* padding: 10px; */
}

@media (max-width: 768px) {
    .cards-container {
        grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    }
}
</style>